import React, { useState } from "react";
import { Column, Row, Text, Card, Divider, Expander, Icon, Button, Loader } from "@amzn/meridian";
import checkLargeTokens from "@amzn/meridian-tokens/base/icon/check-large";
import closeMediumTokens from "@amzn/meridian-tokens/base/icon/close-medium";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import { ObjectPresenceCheck, Evidence } from "src/services/inspectionService";
import ThemedTag from "./ThemedTag";
import ObjectPresenceDetailSheet from "./ObjectPresenceDetailSheet";

interface ObjectPresenceCheckpointCardProps {
    questionNumber: string;
    questionText: string;
    allCompliant: boolean;
    isAnalysisPending?: boolean;
    objectChecks: ObjectPresenceCheck[];
    evidence: Evidence[];
    presignedUrlMap: Record<string, string>;
    onOverride: (check: ObjectPresenceCheck) => void;
}

const ObjectPresenceCheckpointCard: React.FC<ObjectPresenceCheckpointCardProps> = ({
    questionNumber,
    questionText,
    allCompliant,
    isAnalysisPending = false,
    objectChecks,
    evidence,
    presignedUrlMap,
    onOverride,
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [selectedCheck, setSelectedCheck] = useState<ObjectPresenceCheck | null>(null);
    const [sheetOpen, setSheetOpen] = useState(false);

    const handleOverride = (check: ObjectPresenceCheck) => {
        setSheetOpen(false);
        onOverride(check);
    };

    const openSheet = (check: ObjectPresenceCheck) => {
        setSelectedCheck(check);
        setSheetOpen(true);
    };

    return (
        <Card spacingInset="none">
            <Column spacingInset="400" spacing="300">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="h100" color="primary">
                        {questionNumber}
                    </Text>
                    <Row spacing="200" alignmentVertical="center">
                        {isAnalysisPending ? (
                            <Row spacing="200" alignmentVertical="center">
                                <Text type="b100" color="secondary">Analyzing...</Text>
                                <Loader size="small" type="circular" />
                            </Row>
                        ) : allCompliant ? (
                            <ThemedTag text="Compliant" type="success" />
                        ) : (
                            <ThemedTag text="Non Compliant" type="error" />
                        )}
                        {!isAnalysisPending && <ThemedTag text="AI" type="neutral" />}
                    </Row>
                </Row>

                <Text type="b200" color="secondary">
                    {questionText}
                </Text>

                <Divider />

                <Expander
                    title="Detailed compliance status"
                    open={showDetails}
                    onChange={(open: boolean) => setShowDetails(open)}
                    type="inline"
                    spacingInset="none"
                >
                    <Column spacing="none">
                        {objectChecks.map((check, index) => (
                            <Column key={check.object_name} spacingInset="300 none" spacing="400 none">
                                <Row
                                    alignmentHorizontal="justify"
                                    alignmentVertical="center"
                                    spacingInset="200 none"
                                >
                                    <div onClick={() => openSheet(check)}>
                                        <Row alignmentVertical="center" spacing="300" widths={["fit", "fill"]}>
                                            <div
                                                className={`status-circle ${check.is_compliant
                                                        ? "status-circle--compliant"
                                                        : "status-circle--non-compliant"
                                                    }`}
                                            >
                                                {check.is_compliant ? (
                                                    <Icon tokens={checkLargeTokens} color="success" />
                                                ) : (
                                                    <Icon tokens={closeMediumTokens} color="error" />
                                                )}
                                            </div>
                                            <Text type="b200" color="secondary">
                                                {check.object_name}
                                            </Text>
                                        </Row>
                                    </div>
                                    <Button type="icon" onClick={() => openSheet(check)}>
                                        <Icon tokens={chevronRightSmallTokens} color="secondary" />
                                    </Button>
                                </Row>
                                {objectChecks.length - 1 !== index && <Divider />}
                            </Column>
                        ))}
                    </Column>
                </Expander>

            </Column>

            <ObjectPresenceDetailSheet
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                check={selectedCheck}
                onOverride={handleOverride}
                evidence={evidence}
                presignedUrlMap={presignedUrlMap}
            />
        </Card>
    );
};

export default ObjectPresenceCheckpointCard;
